import React, { useState } from 'react';

const TRANSFORM_FUNCTIONS = [
  { value: '', label: 'None (Direct Copy)' },
  { value: 'mm_to_inches', label: 'MM to Inches' },
  { value: 'inches_to_mm', label: 'Inches to MM' },
  { value: 'uppercase', label: 'Uppercase' },
  { value: 'lowercase', label: 'Lowercase' },
  { value: 'trim', label: 'Trim Whitespace' },
  { value: 'extract_number', label: 'Extract Number Only' },
];

const EXCEL_COLUMNS = (() => {
  const cols = [];
  for (let i = 0; i < 52; i++) {
    if (i < 26) cols.push(String.fromCharCode(65 + i));
    else cols.push(String.fromCharCode(64 + Math.floor(i / 26)) + String.fromCharCode(65 + (i % 26)));
  }
  return cols;
})();

const FormatMappingsEditor = ({ mappings: initialMappings, columns, onSave }) => {
  const [mappings, setMappings] = useState(initialMappings || []);
  const [dirty, setDirty] = useState(false);

  const handleAdd = () => {
    setMappings(prev => [...prev, {
      source_field: '',
      target_column: 'A',
      display_name: '',
      transform_function: null,
      sort_order: prev.length,
    }]);
    setDirty(true);
  };

  const handleChange = (index, key, value) => {
    setMappings(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
    setDirty(true);
  };

  const handleRemove = (index) => {
    setMappings(prev => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    setMappings(prev => {
      const updated = [...prev];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      return updated;
    });
    setDirty(true);
  };

  const handleAutoGenerate = () => {
    if (columns.length === 0) { alert('No columns defined. Add columns first.'); return; }
    const auto = columns.map((col, i) => ({
      source_field: col.column_key,
      target_column: EXCEL_COLUMNS[i] || 'A',
      display_name: col.display_name,
      transform_function: null,
      sort_order: i,
    }));
    setMappings(auto);
    setDirty(true);
  };

  const handleSaveClick = async () => {
    const result = await onSave(mappings);
    if (result) { setMappings(result); setDirty(false); }
  };

  const sourceOptions = columns.map(c => c.column_key).filter(Boolean);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: '#495057' }}>Excel Mappings ({mappings.length})</h4>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleAutoGenerate} style={btnOutlineSecondary}>Auto-Generate</button>
          <button onClick={handleAdd} style={btnOutline}>+ Add Mapping</button>
          <button onClick={handleSaveClick} disabled={!dirty} style={{ ...btnPrimary, opacity: dirty ? 1 : 0.5 }}>Save Mappings</button>
        </div>
      </div>

      {mappings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: '#6c757d' }}>
          No mappings defined. Click "Auto-Generate" to create mappings from columns, or "Add Mapping" manually.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Source Field</th>
              <th style={thStyle}>Target Column</th>
              <th style={thStyle}>Display Name</th>
              <th style={thStyle}>Transform</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((m, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #e9ecef' }}>
                <td style={tdStyle}>
                  <button onClick={() => handleMoveUp(i)} disabled={i === 0}
                    style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', fontSize: 14, color: i === 0 ? '#ccc' : '#495057' }}>
                    ▲
                  </button>
                </td>
                <td style={tdStyle}>
                  {sourceOptions.length > 0 ? (
                    <select value={m.source_field || ''} onChange={e => handleChange(i, 'source_field', e.target.value)} style={cellInput}>
                      <option value="">— Select —</option>
                      {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input value={m.source_field || ''} onChange={e => handleChange(i, 'source_field', e.target.value)} placeholder="field_key" style={cellInput} />
                  )}
                </td>
                <td style={tdStyle}>
                  <select value={m.target_column || 'A'} onChange={e => handleChange(i, 'target_column', e.target.value)} style={{ ...cellInput, width: 80 }}>
                    {EXCEL_COLUMNS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td style={tdStyle}>
                  <input value={m.display_name || ''} onChange={e => handleChange(i, 'display_name', e.target.value)} placeholder="Column header" style={cellInput} />
                </td>
                <td style={tdStyle}>
                  <select value={m.transform_function || ''} onChange={e => handleChange(i, 'transform_function', e.target.value || null)} style={cellInput}>
                    {TRANSFORM_FUNCTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <button onClick={() => handleRemove(i)} style={delBtn}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const btnPrimary = { padding: '8px 16px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnOutline = { padding: '8px 16px', backgroundColor: '#fff', color: '#0d6efd', border: '1px solid #0d6efd', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnOutlineSecondary = { padding: '8px 16px', backgroundColor: '#fff', color: '#198754', border: '1px solid #198754', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const thStyle = { padding: '8px 10px', fontSize: 12, fontWeight: 600, color: '#6c757d', textAlign: 'left' };
const tdStyle = { padding: '6px 10px' };
const cellInput = { width: '100%', padding: '6px 8px', border: '1px solid #ced4da', borderRadius: 3, fontSize: 13, fontFamily: 'inherit' };
const delBtn = { padding: '4px 10px', fontSize: 12, border: '1px solid #dc3545', borderRadius: 4, backgroundColor: '#fff', color: '#dc3545', cursor: 'pointer' };

export default FormatMappingsEditor;
